import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  TextField,
  IconButton,
  Divider,
  CircularProgress,
  Alert,
  Badge,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { useTheme, useMediaQuery } from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Chat as ChatIcon,
  Edit as EditIcon,
  Inventory2Outlined as StorageTabIcon,
  LocalShipping as PaymentTabIcon,
  Share as ShareIcon,
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  PhotoCamera as PhotoCameraIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';
import { Category } from '../types/foundObject';
import { useConversationsListQuery } from '../hooks/useConversationsListQuery';
import { useConversationDetailQuery } from '../hooks/useConversationDetailQuery';
import { useProviderInfo } from '../hooks/useProviderInfo';
import { invalidateMessagingCaches, invalidateFoundObjectStats } from '../lib/messagingQueryCache';
import { getAnnouncementImageUrl, getAnnouncementThumbUrl } from '../utils/imageUrl';
import { extractEtiquetteUrlFromContent } from '../utils/colissimoLabel';
import { isDeliveryPaymentMessage } from '../utils/colissimoPaymentMessage';
import { formatAttributeLabel } from '../utils/attributeLabels';
import { isSecretObjectAttributeKey, SECRET_OBJECT_ATTRIBUTE_KEY } from '../utils/secretObjectAttributes';
import ChooseDeliveryCarrierPanel from './ChooseDeliveryCarrierPanel';
import {
  type DeliveryCarrier,
  getAvailableDeliveryCarriers,
} from '../utils/deliveryPricing';
import { useDeliveryPricing } from '../hooks/useDeliveryPricing';
import { buildDeliveryOfferMessage, isDeliveryOfferMessage } from '../utils/deliveryOfferMessage';
import CategoryIcon from './CategoryIcon';
import FormattedReferenceCode from './FormattedReferenceCode';
import {
  ConversationAvatar,
  ConversationMessageList,
  getConversationListButtonSx,
  IMAGE_MESSAGE_PREFIX,
  CONVERSATION_CHAT_BACKGROUND,
} from './conversation/ConversationChatUi';
import { getConversationTheme } from '../utils/conversationTheme';
import { DASHBOARD_BRAND_PRIMARY } from '../theme/dashboardBrand';

function firstImageUrl(images?: string[] | null): string | null {
  const p = images?.find((x) => x && String(x).trim());
  return p ? getAnnouncementImageUrl(p) : null;
}

const LOCATION_META_KEYS = new Set([
  'room',
  'zone',
  'saison',
  'etage',
  'storage_box_id',
  'provider_item_id',
]);

function isLikelySecretAttributeKey(key: string): boolean {
  if (LOCATION_META_KEYS.has(key)) return false;
  return isSecretObjectAttributeKey(key);
}

export interface FoundObjectMessagesWorkspaceProps {
  foundObjectId: number;
  selectedConversationId: number | null;
  /** Passer `null` pour désélectionner (ex. retour liste sur mobile). */
  onSelectConversation: (id: number | null) => void;
  onHandoverConfirmed?: (userName: string) => void;
  /** Flux ouverture depuis une annonce (page Messages) */
  announcementFlow?: boolean;
  conversationPresentInUrl?: boolean;
  startingConversation?: boolean;
  startError?: string | null;
  onDismissStartError?: () => void;
  /** Fiche objet plein écran (page Objets trouvés) */
  variant?: 'default' | 'fullscreen';
  onClose?: () => void;
  /** Onglet « Modifier » : ouvre le formulaire d’édition (parent) */
  onRequestEditFoundObject?: () => void;
}

const FoundObjectMessagesWorkspace: React.FC<FoundObjectMessagesWorkspaceProps> = ({
  foundObjectId,
  selectedConversationId,
  onSelectConversation,
  onHandoverConfirmed,
  announcementFlow = false,
  conversationPresentInUrl = false,
  startingConversation = false,
  startError = null,
  onDismissStartError,
  variant = 'default',
  onClose,
  onRequestEditFoundObject,
}) => {
  const queryClient = useQueryClient();
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const chatImageInputRef = useRef<HTMLInputElement | null>(null);
  const [composeText, setComposeText] = useState('');
  const [sendTextError, setSendTextError] = useState<string | null>(null);
  const [imageUploadPending, setImageUploadPending] = useState(false);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  const [actionRunning, setActionRunning] = useState(false);
  const [confirmUndoOpen, setConfirmUndoOpen] = useState(false);
  const [confirmHandoverOpen, setConfirmHandoverOpen] = useState(false);
  const [deliveryOfferDialogOpen, setDeliveryOfferDialogOpen] = useState(false);
  const [deliveryOfferCarrier, setDeliveryOfferCarrier] = useState<DeliveryCarrier | null>(null);
  const [detailMainTab, setDetailMainTab] = useState(0);
  const autoToShipDoneRef = useRef<number | null>(null);
  const isFullscreen = variant === 'fullscreen';
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));

  const { data: convData, isLoading: loadingList } = useConversationsListQuery({ poll: false });

  const {
    data: detailData,
    isLoading: loadingDetail,
    error: detailError,
  } = useConversationDetailQuery({
    conversationId: selectedConversationId,
    poll: !!selectedConversationId,
  });

  const { data: foundObjectData, isLoading: loadingFoundObject } = useQuery({
    queryKey: ['found-object', foundObjectId],
    queryFn: () => apiService.getFoundObject(foundObjectId).then((r) => r.data.data),
    enabled: !!foundObjectId && !Number.isNaN(foundObjectId),
  });

  const { data: categoriesList } = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiService.getCategories(),
    select: (response) => (response.data?.categories || []) as Category[],
    staleTime: 60_000,
  });

  const categoryForObject = useMemo(
    () => categoriesList?.find((c) => c.name === foundObjectData?.objectType),
    [categoriesList, foundObjectData?.objectType]
  );

  const { data: providerInfoData } = useProviderInfo();

  const sendMutation = useMutation({
    mutationFn: ({ id, content }: { id: number; content: string }) => apiService.sendMessage(id, content),
    onSuccess: () => {
      setSendTextError(null);
      setComposeText('');
      queryClient.invalidateQueries({ queryKey: ['conversation', selectedConversationId] });
      void invalidateMessagingCaches(queryClient);
    },
    onError: (error: unknown) => {
      const ax = error as { response?: { data?: { error?: string; code?: string } } };
      const code = ax.response?.data?.code;
      const apiError = ax.response?.data?.error;
      if (code === 'moderation_rejected' && typeof apiError === 'string' && apiError.trim()) {
        setSendTextError(apiError.trim());
        return;
      }
      setSendTextError(
        (typeof apiError === 'string' && apiError.trim()) ||
          "Impossible d'envoyer le message pour le moment."
      );
    },
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [detailData?.messages?.length]);

  useEffect(() => {
    setImageUploadError(null);
    setSendTextError(null);
  }, [selectedConversationId]);

  const handleChatImageSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !selectedConversationId || !canSendMessages) return;
    setImageUploadError(null);
    setImageUploadPending(true);
    try {
      const uploadRes = await apiService.uploadImage(file, 'messages');
      const uploaded = uploadRes.data;
      const uploadedPath =
        (uploaded.path && String(uploaded.path).trim()) ||
        (uploaded.url && String(uploaded.url).trim()) ||
        (uploaded.filename ? `/uploads/messages/${uploaded.filename}` : '');
      if (!uploadedPath) {
        throw new Error('Réponse serveur inattendue après upload.');
      }
      const imageMessage = `${IMAGE_MESSAGE_PREFIX}${uploadedPath}`;
      await sendMutation.mutateAsync({ id: selectedConversationId, content: imageMessage });
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string; code?: string } }; message?: string };
      if (ax?.response?.data?.code === 'moderation_rejected') {
        const apiError = ax.response?.data?.error;
        setImageUploadError(
          typeof apiError === 'string' && apiError.trim()
            ? apiError.trim()
            : "Ce contenu n'a pas été accepté par la modération automatique."
        );
      } else {
        const msg =
          ax?.response?.data?.error ||
          ax?.message ||
          "Impossible d'envoyer l'image. Vérifiez le format (max 5 Mo) et réessayez.";
        setImageUploadError(typeof msg === 'string' ? msg : "Impossible d'envoyer l'image.");
      }
    } finally {
      setImageUploadPending(false);
    }
  };

  const objectConversations = useMemo(() => {
    const conversations = convData?.conversations;
    if (!conversations?.length) return [];
    return conversations
      .filter((c: any) => {
        const attrs = c.announcement?.object_attributes;
        const fromAttrs = Number(attrs?.found_object_id);
        if (Number.isFinite(fromAttrs) && fromAttrs === foundObjectId) return true;
        const ref = String(c.announcement?.reference_code ?? '');
        return ref === `FO-CONTACT-${foundObjectId}`;
      })
      .sort((a: any, b: any) => new Date(b.updated_at ?? 0).getTime() - new Date(a.updated_at ?? 0).getTime());
  }, [foundObjectId, convData?.conversations]);

  const handleSend = () => {
    if (!selectedConversationId || !composeText.trim()) return;
    sendMutation.mutate({ id: selectedConversationId, content: composeText.trim() });
  };

  const handleQuickSend = async (content: string) => {
    if (!selectedConversationId || !content.trim()) return;
    await sendMutation.mutateAsync({ id: selectedConversationId, content: content.trim() });
  };

  const matchingInTrash = detailData?.conversation?.matching_in_trash;
  const messagingLockedReason = detailData?.conversation?.messaging_locked_reason ?? null;
  const isRestitutionElsewhereLocked = messagingLockedReason === 'restitution_elsewhere';
  const isMessagingLocked =
    isRestitutionElsewhereLocked ||
    detailData?.conversation?.announcement?.messaging_allowed === false ||
    (detailData?.conversation?.announcement?.status != null &&
      detailData.conversation.announcement.status !== 'active');
  const canSendMessages = !!selectedConversationId && !matchingInTrash && !isMessagingLocked;
  const chatMessages = (providerInfoData?.settings?.chat_messages || {}) as {
    validation_message?: string;
    rejection_message?: string;
    handover_message?: string;
    undo_handover_message?: string;
    delivery_voucher_message?: string;
  };
  const validationQuickMessage =
    (chatMessages.validation_message && chatMessages.validation_message.trim()) ||
    'Bonjour, votre description correspond. Vous pouvez venir récupérer votre objet.';
  const rejectionQuickMessage =
    (chatMessages.rejection_message && chatMessages.rejection_message.trim()) ||
    "Bonjour, les informations ne correspondent pas à l'objet trouvé.";
  const handoverQuickMessage =
    (chatMessages.handover_message && chatMessages.handover_message.trim()) ||
    'Objet restitué en main propre. Merci !';
  const undoHandoverQuickMessage =
    (chatMessages.undo_handover_message && chatMessages.undo_handover_message.trim()) ||
    'La restitution en main propre a été annulée.';
  const deliveryVoucherQuickMessage =
    (chatMessages.delivery_voucher_message && chatMessages.delivery_voucher_message.trim()) ||
    "Bon de livraison : d'accord pour un bon Colissimo/UPS/Mondial Relay. Le paiement se fera sur Stripe par le perdeur, et une fois payé je pourrai imprimer le bon.";

  const deliveryLocationProviderId =
    foundObjectData?.location_provider?.id ?? providerInfoData?.id ?? undefined;

  const { data: deliveryPricing } = useDeliveryPricing({
    locationProviderId: deliveryLocationProviderId,
  });

  const colissimoPaymentMessage = useMemo(() => {
    const list = detailData?.messages ?? [];
    const lastSuccessful = [...list]
      .reverse()
      .find(
        (m: any) =>
          typeof m.content === 'string' &&
          isDeliveryPaymentMessage(m.content) &&
          /Etiquette:\s*\S+/i.test(m.content)
      );
    if (lastSuccessful) return lastSuccessful;
    return [...list]
      .reverse()
      .find((m: any) => typeof m.content === 'string' && isDeliveryPaymentMessage(m.content));
  }, [detailData?.messages]);

  const colissimoEtiquetteUrl = colissimoPaymentMessage?.content
    ? extractEtiquetteUrlFromContent(colissimoPaymentMessage.content)
    : null;

  const deliveryOfferAlreadySent = useMemo(() => {
    const messages = detailData?.messages ?? [];
    return messages.some(
      (m: { content?: string; is_from_me?: boolean }) =>
        !!m.is_from_me &&
        typeof m.content === 'string' &&
        isDeliveryOfferMessage(m.content)
    );
  }, [detailData?.messages]);

  const handleDeliveryOfferAction = async () => {
    if (deliveryOfferAlreadySent) return;
    const carriers = getAvailableDeliveryCarriers(deliveryPricing);
    if (carriers.length === 0) return;
    const customBody = deliveryVoucherQuickMessage.replace(/^Bon de livraison :\s*/i, '').trim();
    if (carriers.length === 1) {
      await handleQuickSend(buildDeliveryOfferMessage(carriers[0], customBody));
      return;
    }
    setDeliveryOfferCarrier(null);
    setDeliveryOfferDialogOpen(true);
  };

  const sendDeliveryOffer = async () => {
    if (!deliveryOfferCarrier) return;
    const customBody = deliveryVoucherQuickMessage.replace(/^Bon de livraison :\s*/i, '').trim();
    await handleQuickSend(buildDeliveryOfferMessage(deliveryOfferCarrier, customBody));
    setDeliveryOfferDialogOpen(false);
  };

  const isClaimed = foundObjectData?.status === 'claimed';

  const { publicAttrEntries, secretAttrEntries } = useMemo(() => {
    const raw = foundObjectData?.objectAttributes || {};
    const catAttrs = categoryForObject?.attributes;
    const publicList: [string, string][] = [];
    const secretList: [string, string][] = [];
    for (const [key, val] of Object.entries(raw)) {
      if (LOCATION_META_KEYS.has(key)) continue;
      const str = val == null ? '' : String(val);
      if (!str.trim()) continue;
      const def = catAttrs?.[key];
      const isSecret =
        def?.secret_detail === true || (def == null && isLikelySecretAttributeKey(key));
      if (isSecret) secretList.push([key, str]);
      else publicList.push([key, str]);
    }
    return { publicAttrEntries: publicList, secretAttrEntries: secretList };
  }, [foundObjectData?.objectAttributes, categoryForObject?.attributes]);

  const linkedAnnouncementSecretClues = useMemo(() => {
    const attrs = detailData?.conversation?.announcement?.object_attributes;
    if (!attrs || typeof attrs !== 'object') return '';
    const raw = (attrs as Record<string, unknown>)[SECRET_OBJECT_ATTRIBUTE_KEY];
    return raw == null ? '' : String(raw).trim();
  }, [detailData?.conversation?.announcement?.object_attributes]);

  const handleShareObjectLink = () => {
    const url = `${window.location.origin}${window.location.pathname}?found_object=${foundObjectId}`;
    void (async () => {
      try {
        if (navigator.share) {
          await navigator.share({ title: foundObjectData?.title || 'Objet Sherlook', url });
          return;
        }
      } catch {
        /* annulé ou indisponible */
      }
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        /* ignore */
      }
    })();
  };

  useEffect(() => {
    const objectId = foundObjectId;
    if (!objectId || objectId <= 0) return;
    if (!colissimoEtiquetteUrl) return;
    if (!foundObjectData?.status) return;
    if (
      foundObjectData.status === 'to_ship' ||
      foundObjectData.status === 'in_transit' ||
      foundObjectData.status === 'claimed'
    ) {
      return;
    }
    if (autoToShipDoneRef.current === objectId) return;

    autoToShipDoneRef.current = objectId;
    apiService
      .updateFoundObject(objectId, { status: 'to_ship' })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['found-object', objectId] });
        queryClient.invalidateQueries({ queryKey: ['found-objects'] });
        void invalidateFoundObjectStats(queryClient);
      })
      .catch(() => {
        autoToShipDoneRef.current = null;
      });
  }, [foundObjectId, colissimoEtiquetteUrl, foundObjectData?.status, queryClient]);

  const handleMarkClaimed = async () => {
    if (!foundObjectId) return;
    setActionRunning(true);
    try {
      const currentUser = apiService.getCurrentUser();
      const actorName = [currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(' ').trim();
      await apiService.updateFoundObject(foundObjectId, { status: 'claimed' });
      if (actorName) {
        await apiService.updateFoundObject(foundObjectId, { claimant_name: actorName } as any);
      }
      await queryClient.invalidateQueries({ queryKey: ['found-object', foundObjectId] });
      await queryClient.invalidateQueries({ queryKey: ['found-objects'] });
      await invalidateFoundObjectStats(queryClient);
      if (selectedConversationId) {
        await handleQuickSend(handoverQuickMessage);
      }
      onHandoverConfirmed?.(actorName || 'Champion');
    } finally {
      setActionRunning(false);
    }
  };

  const handleUndoClaimed = async () => {
    if (!foundObjectId) return;
    setActionRunning(true);
    try {
      await apiService.updateFoundObject(foundObjectId, { status: 'found' });
      await queryClient.invalidateQueries({ queryKey: ['found-object', foundObjectId] });
      await queryClient.invalidateQueries({ queryKey: ['found-objects'] });
      await invalidateFoundObjectStats(queryClient);
      if (selectedConversationId) {
        await handleQuickSend(undoHandoverQuickMessage);
      }
    } finally {
      setActionRunning(false);
      setConfirmUndoOpen(false);
    }
  };

  const activeConversationTheme = useMemo(
    () => (selectedConversationId ? getConversationTheme(selectedConversationId) : null),
    [selectedConversationId]
  );

  return (
    <>
      {!isFullscreen ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            minHeight: { xs: 360, md: 480 },
            maxWidth: '100%',
            flex: 1,
            overflow: 'auto',
          }}
        >
      <Card sx={{ width: { xs: '100%', md: 320 }, maxWidth: '100%', flexShrink: 0 }}>
        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Objet
          </Typography>
          {loadingFoundObject || !foundObjectData ? (
            <CircularProgress size={22} />
          ) : (
            <>
              <Box
                component="img"
                src={
                  getAnnouncementThumbUrl(
                    foundObjectData?.images || detailData?.conversation?.announcement?.images || [],
                    undefined
                  ) || undefined
                }
                alt=""
                sx={{
                  width: '100%',
                  height: { xs: 140, sm: 180 },
                  objectFit: 'cover',
                  borderRadius: 2,
                  bgcolor: 'grey.100',
                  mb: 1.5,
                }}
              />
              <Typography variant="h6">
                {foundObjectData?.title || detailData?.conversation?.announcement?.title || 'Objet'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {detailData?.conversation?.announcement?.description || 'Aucun détail'}
              </Typography>
              {foundObjectData?.reference_code && (
                <FormattedReferenceCode
                  code={foundObjectData.reference_code}
                  labelPrefix="Ref: "
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 1 }}
                />
              )}
            </>
          )}
        </Box>
      </Card>

      <Card
        sx={{
          width: { xs: '100%', md: 360 },
          maxWidth: '100%',
          flexShrink: 0,
          maxHeight: { xs: '36vh', md: '78vh' },
          overflow: 'auto',
        }}
      >
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Conversations ({objectConversations.length})
          </Typography>
        </Box>
        {loadingList ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : objectConversations.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Aucune conversation pour cet objet.
            </Typography>
          </Box>
        ) : (
          <List dense>
            {objectConversations.map((c: any) => (
              <ListItem key={c.id} disablePadding>
                <ListItemButton
                  selected={selectedConversationId === c.id}
                  onClick={() => onSelectConversation(c.id)}
                  sx={getConversationListButtonSx(c.id, selectedConversationId === c.id)}
                >
                  <ListItemAvatar>
                    <Badge
                      color="error"
                      variant="dot"
                      invisible={!c.unread_count || c.unread_count === 0}
                      overlap="circular"
                    >
                      <ConversationAvatar
                        conversationId={c.id}
                        imageSrc={firstImageUrl(c.announcement?.images)}
                        initial={c.other_user?.first_name}
                      />
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={c.other_user ? `${c.other_user.first_name} ${c.other_user.last_name}` : 'Utilisateur'}
                    secondary={c.last_message?.content?.slice(0, 60) || '…'}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Card>

      <Card
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: 280, sm: 400 },
          maxWidth: '100%',
          minWidth: 0,
        }}
      >
        {announcementFlow && !conversationPresentInUrl && startingConversation && (
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={24} />
            <Typography>Ouverture de la conversation…</Typography>
          </Box>
        )}

        {startError && (
          <Alert severity="error" sx={{ m: 2 }} onClose={onDismissStartError}>
            {startError}
          </Alert>
        )}

        {!selectedConversationId && !announcementFlow && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              Sélectionnez une conversation ou ouvrez-en une depuis Mes matchings.
            </Typography>
          </Box>
        )}

        {selectedConversationId && (
          <>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              {loadingDetail ? (
                <CircularProgress size={24} />
              ) : detailError ? (
                <Alert severity="error">Conversation introuvable ou accès refusé.</Alert>
              ) : (
                <>
                  <Typography variant="h6">
                    {detailData?.conversation?.other_user
                      ? `${detailData.conversation.other_user.first_name} ${detailData.conversation.other_user.last_name}`
                      : 'Conversation'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {detailData?.conversation?.announcement?.title}
                  </Typography>
                  {matchingInTrash && (
                    <Chip
                      sx={{ mt: 1 }}
                      label="Matching en corbeille — messagerie limitée"
                      color="warning"
                      size="small"
                    />
                  )}
                  {isRestitutionElsewhereLocked && (
                    <Chip
                      sx={{ mt: 1 }}
                      label="Objet restitué via une autre conversation"
                      color="warning"
                      size="small"
                    />
                  )}
                </>
              )}
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: CONVERSATION_CHAT_BACKGROUND }}>
              {isRestitutionElsewhereLocked && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Cet objet a été restitué à son propriétaire via une autre conversation. La messagerie est en lecture
                  seule.
                </Alert>
              )}
              {loadingDetail ? (
                <CircularProgress />
              ) : activeConversationTheme ? (
                <>
                  <ConversationMessageList
                    messages={detailData?.messages || []}
                    theme={activeConversationTheme}
                    deliveryPricing={deliveryPricing}
                    deliveryLocationProviderId={deliveryLocationProviderId}
                  />
                  <div ref={messagesEndRef} />
                </>
              ) : null}
            </Box>

            {imageUploadError && (
              <Alert severity="error" onClose={() => setImageUploadError(null)} sx={{ mx: 2 }}>
                {imageUploadError}
              </Alert>
            )}
            {sendTextError && (
              <Alert severity="warning" onClose={() => setSendTextError(null)} sx={{ mx: 2 }}>
                {sendTextError}
              </Alert>
            )}
            <Divider />
            <input
              ref={chatImageInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleChatImageSelected}
            />
            <Box sx={{ p: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
              <IconButton
                color="primary"
                size="small"
                aria-label="Envoyer une photo"
                onClick={() => chatImageInputRef.current?.click()}
                disabled={!canSendMessages || imageUploadPending || sendMutation.isPending}
              >
                {imageUploadPending ? <CircularProgress size={22} /> : <PhotoCameraIcon />}
              </IconButton>
              <TextField
                fullWidth
                size="small"
                placeholder="Votre message…"
                value={composeText}
                onChange={(e) => {
                  setSendTextError(null);
                  setComposeText(e.target.value);
                }}
                disabled={!canSendMessages || sendMutation.isPending}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
              />
              <IconButton
                color="primary"
                onClick={handleSend}
                disabled={!canSendMessages || !composeText.trim() || sendMutation.isPending}
              >
                <SendIcon />
              </IconButton>
            </Box>
            <Box sx={{ px: { xs: 1, sm: 2 }, pb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="contained"
                color="secondary"
                onClick={isClaimed ? () => setConfirmUndoOpen(true) : () => setConfirmHandoverOpen(true)}
                disabled={
                  !foundObjectId ||
                  actionRunning ||
                  sendMutation.isPending
                }
              >
                {isClaimed ? 'Annuler la restitution' : 'Restitué en main propre'}
              </Button>
              <Button
                size="small"
                variant="contained"
                color="success"
                onClick={() => handleQuickSend(validationQuickMessage)}
                disabled={!canSendMessages || actionRunning || sendMutation.isPending}
              >
                Je valide
              </Button>
              <Button
                size="small"
                variant="contained"
                color="error"
                onClick={() => handleQuickSend(rejectionQuickMessage)}
                disabled={!canSendMessages || actionRunning || sendMutation.isPending}
              >
                Je ne valide pas
              </Button>
              <Button
                size="small"
                variant="contained"
                component={colissimoEtiquetteUrl ? 'a' : 'button'}
                href={colissimoEtiquetteUrl || undefined}
                target={colissimoEtiquetteUrl ? '_blank' : undefined}
                rel={colissimoEtiquetteUrl ? 'noreferrer' : undefined}
                onClick={colissimoEtiquetteUrl ? undefined : () => void handleDeliveryOfferAction()}
                disabled={
                  !canSendMessages ||
                  actionRunning ||
                  sendMutation.isPending ||
                  (deliveryOfferAlreadySent && !colissimoEtiquetteUrl)
                }
              >
                {colissimoEtiquetteUrl
                  ? 'Imprimer le bon'
                  : deliveryOfferAlreadySent
                    ? 'Bon de livraison envoyé'
                    : 'Bon de livraison'}
              </Button>
            </Box>
          </>
        )}

        {!selectedConversationId && !announcementFlow && (
          <Box sx={{ px: { xs: 1, sm: 2 }, pb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              color="secondary"
              onClick={isClaimed ? () => setConfirmUndoOpen(true) : () => setConfirmHandoverOpen(true)}
              disabled={!foundObjectId || actionRunning}
            >
              {isClaimed ? 'Annuler la restitution' : 'Restitué en main propre'}
            </Button>
            <Typography variant="caption" color="text.secondary">
              La restitution mettra a jour le statut de l'objet meme sans conversation.
            </Typography>
          </Box>
        )}
      </Card>
        </Box>
      ) : (
        <Box
          sx={{
            height: '100%',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: 'background.paper',
          }}
        >
          {isMdDown && (
            <Box
              sx={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1.5,
                py: 1,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                zIndex: 3,
              }}
            >
              {loadingFoundObject || !foundObjectData ? (
                <CircularProgress size={28} sx={{ ml: 0.5 }} />
              ) : (
                <>
                  <Box
                    component="img"
                    alt=""
                    src={
                      getAnnouncementThumbUrl(
                        foundObjectData.images || detailData?.conversation?.announcement?.images || [],
                        categoryForObject?.default_image_url
                      ) || undefined
                    }
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1,
                      objectFit: 'cover',
                      bgcolor: 'grey.200',
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <FormattedReferenceCode
                      code={
                        foundObjectData.reference_code
                          ? String(foundObjectData.reference_code)
                          : String(foundObjectData.id)
                      }
                      hashPrefix
                      stripFoPrefix
                      variant="subtitle2"
                      sx={{ fontWeight: 700, lineHeight: 1.2 }}
                      noWrap
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, minWidth: 0 }}>
                      <CategoryIcon
                        icon={categoryForObject?.icon}
                        customIconUrl={categoryForObject?.custom_icon_url}
                        customIconFilename={categoryForObject?.custom_icon_filename}
                        size="small"
                        color="primary.main"
                      />
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
                        {foundObjectData.objectType || '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                        {new Date(foundObjectData.foundAt).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton size="small" onClick={handleShareObjectLink} aria-label="Partager le lien">
                    <ShareIcon fontSize="small" />
                  </IconButton>
                  {onClose && (
                    <IconButton edge="end" onClick={onClose} aria-label="Fermer" size="small">
                      <CloseIcon />
                    </IconButton>
                  )}
                </>
              )}
            </Box>
          )}

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'row',
              overflow: 'hidden',
            }}
          >
          {!isMdDown && (
          <Box
            sx={{
              width: 300,
              maxWidth: 320,
              flexShrink: 0,
              borderRight: 1,
              borderColor: 'divider',
              overflow: 'auto',
              bgcolor: 'grey.50',
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              {onClose && (
                <IconButton edge="end" onClick={onClose} aria-label="Fermer" size="small">
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
            {loadingFoundObject || !foundObjectData ? (
              <CircularProgress size={22} />
            ) : (
              <>
                <Box
                  component="img"
                  src={
                    getAnnouncementThumbUrl(
                      foundObjectData?.images || detailData?.conversation?.announcement?.images || [],
                      categoryForObject?.default_image_url
                    ) || undefined
                  }
                  alt=""
                  sx={{
                    width: '100%',
                    height: 200,
                    objectFit: 'cover',
                    borderRadius: 2,
                    bgcolor: 'grey.200',
                    mb: 1,
                  }}
                />
                <Chip
                  size="small"
                  label={foundObjectData.objectType || '—'}
                  sx={{ mb: 1.5, fontWeight: 600 }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  <CategoryIcon
                    icon={categoryForObject?.icon}
                    customIconUrl={categoryForObject?.custom_icon_url}
                    customIconFilename={categoryForObject?.custom_icon_filename}
                    size="small"
                    color="primary.main"
                  />
                  <FormattedReferenceCode
                    code={
                      foundObjectData.reference_code
                        ? String(foundObjectData.reference_code)
                        : String(foundObjectData.id)
                    }
                    hashPrefix
                    stripFoPrefix
                    variant="subtitle1"
                    sx={{ fontWeight: 600 }}
                  />
                  <IconButton size="small" onClick={handleShareObjectLink} aria-label="Partager le lien">
                    <ShareIcon fontSize="small" />
                  </IconButton>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(foundObjectData.foundAt).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </Typography>
                </Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                  Caractéristiques
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                  {publicAttrEntries.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Aucune caractéristique renseignée.
                    </Typography>
                  ) : (
                    publicAttrEntries.map(([key, val]) => (
                      <Box
                        key={key}
                        sx={{
                          p: 1.25,
                          borderRadius: 2,
                          bgcolor: 'background.paper',
                          border: 1,
                          borderColor: 'divider',
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" display="block">
                          {formatAttributeLabel(key, {
                            categoryAttributes: categoryForObject?.attributes,
                            objectAttributes: foundObjectData?.objectAttributes,
                          })}
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {val}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                  Détails secrets
                </Typography>
                <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: 'background.paper' }}>
                  <Box sx={{ p: 1.5 }}>
                    {secretAttrEntries.length === 0 && !linkedAnnouncementSecretClues ? (
                      <Typography variant="body2" color="text.secondary">
                        Aucun détail secret renseigné.
                      </Typography>
                    ) : (
                      <>
                        {linkedAnnouncementSecretClues ? (
                          <Box sx={{ mb: secretAttrEntries.length > 0 ? 1.5 : 0 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Détail secret (déclarant)
                            </Typography>
                            <Typography variant="body2">{linkedAnnouncementSecretClues}</Typography>
                          </Box>
                        ) : null}
                        {secretAttrEntries.map(([key, val]) => (
                          <Box key={key} sx={{ mb: secretAttrEntries.length > 1 ? 1.5 : 0 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {formatAttributeLabel(key, {
                            categoryAttributes: categoryForObject?.attributes,
                            objectAttributes: foundObjectData?.objectAttributes,
                          })}
                            </Typography>
                            <Typography variant="body2">{val}</Typography>
                          </Box>
                        ))}
                      </>
                    )}
                  </Box>
                </Card>
              </>
            )}
          </Box>
          )}

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            {isMdDown && (
              <Accordion
                disableGutters
                elevation={0}
                sx={{
                  flexShrink: 0,
                  borderBottom: 1,
                  borderColor: 'divider',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, px: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Fiche objet
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{ px: 2, pb: 2, pt: 0, bgcolor: 'grey.50', maxHeight: '40vh', overflow: 'auto' }}
                >
                  {loadingFoundObject || !foundObjectData ? (
                    <CircularProgress size={22} />
                  ) : (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                        Caractéristiques
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                        {publicAttrEntries.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            Aucune caractéristique renseignée.
                          </Typography>
                        ) : (
                          publicAttrEntries.map(([key, val]) => (
                            <Box
                              key={key}
                              sx={{
                                p: 1.25,
                                borderRadius: 2,
                                bgcolor: 'background.paper',
                                border: 1,
                                borderColor: 'divider',
                              }}
                            >
                              <Typography variant="caption" color="text.secondary" display="block">
                                {formatAttributeLabel(key, {
                            categoryAttributes: categoryForObject?.attributes,
                            objectAttributes: foundObjectData?.objectAttributes,
                          })}
                              </Typography>
                              <Typography variant="body2" fontWeight={500}>
                                {val}
                              </Typography>
                            </Box>
                          ))
                        )}
                      </Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                        Détails secrets
                      </Typography>
                      <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: 'background.paper' }}>
                        <Box sx={{ p: 1.5 }}>
                          {secretAttrEntries.length === 0 && !linkedAnnouncementSecretClues ? (
                            <Typography variant="body2" color="text.secondary">
                              Aucun détail secret renseigné.
                            </Typography>
                          ) : (
                            <>
                              {linkedAnnouncementSecretClues ? (
                                <Box sx={{ mb: secretAttrEntries.length > 0 ? 1.5 : 0 }}>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Détail secret (déclarant)
                                  </Typography>
                                  <Typography variant="body2">{linkedAnnouncementSecretClues}</Typography>
                                </Box>
                              ) : null}
                              {secretAttrEntries.map(([key, val]) => (
                                <Box key={key} sx={{ mb: secretAttrEntries.length > 1 ? 1.5 : 0 }}>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {formatAttributeLabel(key, {
                            categoryAttributes: categoryForObject?.attributes,
                            objectAttributes: foundObjectData?.objectAttributes,
                          })}
                                  </Typography>
                                  <Typography variant="body2">{val}</Typography>
                                </Box>
                              ))}
                            </>
                          )}
                        </Box>
                      </Card>
                    </>
                  )}
                </AccordionDetails>
              </Accordion>
            )}
            <Tabs
              value={detailMainTab}
              onChange={(_, v) => setDetailMainTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                flexShrink: 0,
                borderBottom: 1,
                borderColor: 'divider',
                minHeight: 48,
                '& .MuiTab-root': {
                  minHeight: 48,
                  textTransform: 'none',
                  color: DASHBOARD_BRAND_PRIMARY,
                  opacity: 0.72,
                  '&.Mui-selected': {
                    color: DASHBOARD_BRAND_PRIMARY,
                    opacity: 1,
                  },
                },
                '& .MuiTabs-indicator': { bgcolor: DASHBOARD_BRAND_PRIMARY },
              }}
            >
              <Tab label="Discuter" icon={<ChatIcon fontSize="small" />} iconPosition="start" />
              <Tab label="Modifier" icon={<EditIcon fontSize="small" />} iconPosition="start" />
              <Tab label="Stockage" icon={<StorageTabIcon fontSize="small" />} iconPosition="start" />
              <Tab label="Suivi de paiement" icon={<PaymentTabIcon fontSize="small" />} iconPosition="start" />
            </Tabs>

            {detailMainTab === 0 && (
              <>
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    minHeight: 0,
                    overflow: 'hidden',
                  }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      width: { xs: '100%', md: 300 },
                      maxWidth: '100%',
                      flexShrink: 0,
                      borderRadius: 0,
                      borderRight: { md: 1 },
                      borderBottom: { xs: 1, md: 0 },
                      borderColor: 'divider',
                      display: {
                        xs: selectedConversationId ? 'none' : 'flex',
                        md: 'flex',
                      },
                      flexDirection: 'column',
                      minHeight: { xs: 0, md: 0 },
                      flex: { xs: selectedConversationId ? 0 : 1, md: 'unset' },
                      maxHeight: { xs: 'none', md: 'none' },
                    }}
                  >
                    <Box
                      sx={{
                        p: 1.5,
                        borderBottom: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={600}>
                        Conversations ({objectConversations.length})
                      </Typography>
                      <IconButton
                        size="small"
                        aria-label="Actualiser"
                        onClick={() => void invalidateMessagingCaches(queryClient)}
                      >
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                      {loadingList ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : objectConversations.length === 0 ? (
                        <Box sx={{ p: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Aucune conversation pour cet objet.
                          </Typography>
                        </Box>
                      ) : (
                        <List dense disablePadding>
                          {objectConversations.map((c: any) => (
                            <ListItem key={c.id} disablePadding>
                              <ListItemButton
                                selected={selectedConversationId === c.id}
                                onClick={() => onSelectConversation(c.id)}
                                sx={getConversationListButtonSx(c.id, selectedConversationId === c.id)}
                              >
                                <ListItemAvatar>
                                  <Badge
                                    color="error"
                                    variant="dot"
                                    invisible={!c.unread_count || c.unread_count === 0}
                                    overlap="circular"
                                  >
                                    <ConversationAvatar
                                      conversationId={c.id}
                                      imageSrc={firstImageUrl(c.announcement?.images)}
                                      initial={c.other_user?.first_name}
                                    />
                                  </Badge>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={
                                    c.other_user?.email ||
                                    (c.other_user
                                      ? `${c.other_user.first_name} ${c.other_user.last_name}`
                                      : 'Utilisateur')
                                  }
                                  secondary={c.last_message?.content?.slice(0, 60) || '…'}
                                  primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                                  secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                                />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </Box>
                  </Card>

                  <Card
                    elevation={0}
                    sx={{
                      flex: 1,
                      display: {
                        xs: selectedConversationId ? 'flex' : 'none',
                        md: 'flex',
                      },
                      flexDirection: 'column',
                      minWidth: 0,
                      minHeight: 0,
                      borderRadius: 0,
                    }}
                  >
                    {announcementFlow && !conversationPresentInUrl && startingConversation && (
                      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={24} />
                        <Typography>Ouverture de la conversation…</Typography>
                      </Box>
                    )}
                    {startError && (
                      <Alert severity="error" sx={{ m: 2 }} onClose={onDismissStartError}>
                        {startError}
                      </Alert>
                    )}
                    {!selectedConversationId && !announcementFlow && (
                      <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography color="text.secondary">
                          Sélectionnez une conversation pour afficher les messages.
                        </Typography>
                      </Box>
                    )}
                    {selectedConversationId && (
                      <>
                        {isMdDown ? (
                          <Box
                            sx={{
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              borderBottom: 1,
                              borderColor: 'divider',
                              px: 0.5,
                              minHeight: 48,
                            }}
                          >
                            <IconButton
                              aria-label="Retour aux conversations"
                              onClick={() => onSelectConversation(null)}
                              size="small"
                              edge="start"
                            >
                              <ArrowBackIcon />
                            </IconButton>
                            <Box sx={{ flex: 1, minWidth: 0, py: 0.5 }}>
                              {loadingDetail ? (
                                <CircularProgress size={22} />
                              ) : detailError ? (
                                <Alert severity="error" sx={{ py: 0 }}>
                                  Conversation introuvable.
                                </Alert>
                              ) : (
                                <>
                                  <Typography variant="subtitle1" fontWeight={600} noWrap>
                                    {detailData?.conversation?.other_user
                                      ? `${detailData.conversation.other_user.first_name} ${detailData.conversation.other_user.last_name}`
                                      : 'Conversation'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                    {detailData?.conversation?.announcement?.title}
                                  </Typography>
                                  {matchingInTrash && (
                                    <Chip
                                      sx={{ mt: 0.5 }}
                                      label="Matching en corbeille"
                                      color="warning"
                                      size="small"
                                    />
                                  )}
                                </>
                              )}
                            </Box>
                          </Box>
                        ) : (
                          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                            {loadingDetail ? (
                              <CircularProgress size={24} />
                            ) : detailError ? (
                              <Alert severity="error">Conversation introuvable ou accès refusé.</Alert>
                            ) : (
                              <>
                                <Typography variant="subtitle1" fontWeight={600}>
                                  {detailData?.conversation?.other_user
                                    ? `${detailData.conversation.other_user.first_name} ${detailData.conversation.other_user.last_name}`
                                    : 'Conversation'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {detailData?.conversation?.announcement?.title}
                                </Typography>
                                {matchingInTrash && (
                                  <Chip
                                    sx={{ mt: 1 }}
                                    label="Matching en corbeille — messagerie limitée"
                                    color="warning"
                                    size="small"
                                  />
                                )}
                              </>
                            )}
                          </Box>
                        )}
                        <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: CONVERSATION_CHAT_BACKGROUND }}>
                          {loadingDetail ? (
                            <CircularProgress />
                          ) : activeConversationTheme ? (
                            <>
                              <ConversationMessageList
                                messages={detailData?.messages || []}
                                theme={activeConversationTheme}
                                deliveryPricing={deliveryPricing}
                                deliveryLocationProviderId={deliveryLocationProviderId}
                              />
                              <div ref={messagesEndRef} />
                            </>
                          ) : null}
                        </Box>
                        {imageUploadError && (
                          <Alert
                            severity="error"
                            onClose={() => setImageUploadError(null)}
                            sx={{ mx: 2, flexShrink: 0 }}
                          >
                            {imageUploadError}
                          </Alert>
                        )}
                        {sendTextError && (
                          <Alert
                            severity="warning"
                            onClose={() => setSendTextError(null)}
                            sx={{ mx: 2, flexShrink: 0 }}
                          >
                            {sendTextError}
                          </Alert>
                        )}
                        <Divider />
                        <input
                          ref={chatImageInputRef}
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={handleChatImageSelected}
                        />
                        <Box
                          sx={{
                            p: 2,
                            display: 'flex',
                            gap: 1,
                            flexShrink: 0,
                            bgcolor: 'background.paper',
                            alignItems: 'center',
                          }}
                        >
                          <IconButton
                            color="primary"
                            size="small"
                            aria-label="Envoyer une photo"
                            onClick={() => chatImageInputRef.current?.click()}
                            disabled={!canSendMessages || imageUploadPending || sendMutation.isPending}
                          >
                            {imageUploadPending ? <CircularProgress size={22} /> : <PhotoCameraIcon />}
                          </IconButton>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Écrivez votre message…"
                            value={composeText}
                            onChange={(e) => {
                              setSendTextError(null);
                              setComposeText(e.target.value);
                            }}
                            disabled={!canSendMessages || sendMutation.isPending}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSend();
                              }
                            }}
                          />
                          <IconButton
                            color="primary"
                            onClick={handleSend}
                            disabled={!canSendMessages || !composeText.trim() || sendMutation.isPending}
                          >
                            <SendIcon />
                          </IconButton>
                        </Box>
                      </>
                    )}
                  </Card>
                </Box>

                <Box
                  sx={{
                    flexShrink: 0,
                    // Toujours visible (y compris mobile sans conversation) :
                    // la restitution en main propre ne dépend pas d'un fil de discussion.
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                    gap: 0.75,
                    p: { xs: 1, sm: 1.25 },
                    borderTop: 1,
                    borderColor: 'divider',
                    bgcolor: 'grey.50',
                  }}
                >
                  <Button
                    fullWidth
                    size="small"
                    variant="contained"
                    sx={{
                      bgcolor: '#7b1fa2',
                      '&:hover': { bgcolor: '#6a1b9a' },
                      py: { xs: 0.75, sm: 1 },
                      px: 0.5,
                      minWidth: 0,
                      fontSize: { xs: '0.65rem', sm: '0.75rem' },
                      lineHeight: 1.15,
                      fontWeight: 700,
                      textTransform: 'none',
                      whiteSpace: 'normal',
                    }}
                    onClick={isClaimed ? () => setConfirmUndoOpen(true) : () => setConfirmHandoverOpen(true)}
                    disabled={!foundObjectId || actionRunning || sendMutation.isPending}
                  >
                    {isClaimed ? 'Annuler restitution' : 'Restitué main propre'}
                  </Button>
                  <Button
                    fullWidth
                    size="small"
                    variant="contained"
                    color="success"
                    sx={{
                      py: { xs: 0.75, sm: 1 },
                      px: 0.5,
                      minWidth: 0,
                      fontSize: { xs: '0.65rem', sm: '0.75rem' },
                      lineHeight: 1.15,
                      fontWeight: 700,
                      textTransform: 'none',
                      whiteSpace: 'normal',
                    }}
                    onClick={() => handleQuickSend(validationQuickMessage)}
                    disabled={!canSendMessages || actionRunning || sendMutation.isPending}
                  >
                    Je valide
                  </Button>
                  <Button
                    fullWidth
                    size="small"
                    variant="contained"
                    color="error"
                    sx={{
                      py: { xs: 0.75, sm: 1 },
                      px: 0.5,
                      minWidth: 0,
                      fontSize: { xs: '0.65rem', sm: '0.75rem' },
                      lineHeight: 1.15,
                      fontWeight: 700,
                      textTransform: 'none',
                      whiteSpace: 'normal',
                    }}
                    onClick={() => handleQuickSend(rejectionQuickMessage)}
                    disabled={!canSendMessages || actionRunning || sendMutation.isPending}
                  >
                    Je ne valide pas
                  </Button>
                  <Button
                    fullWidth
                    size="small"
                    variant="contained"
                    sx={{
                      bgcolor: '#1976d2',
                      py: { xs: 0.75, sm: 1 },
                      px: 0.5,
                      minWidth: 0,
                      fontSize: { xs: '0.65rem', sm: '0.75rem' },
                      lineHeight: 1.15,
                      fontWeight: 700,
                      textTransform: 'none',
                      whiteSpace: 'normal',
                    }}
                    component={colissimoEtiquetteUrl ? 'a' : 'button'}
                    href={colissimoEtiquetteUrl || undefined}
                    target={colissimoEtiquetteUrl ? '_blank' : undefined}
                    rel={colissimoEtiquetteUrl ? 'noreferrer' : undefined}
                    onClick={colissimoEtiquetteUrl ? undefined : () => void handleDeliveryOfferAction()}
                    disabled={
                      !canSendMessages ||
                      actionRunning ||
                      sendMutation.isPending ||
                      (deliveryOfferAlreadySent && !colissimoEtiquetteUrl)
                    }
                  >
                    {colissimoEtiquetteUrl
                      ? 'Imprimer le bon'
                      : deliveryOfferAlreadySent
                        ? 'Bon envoyé'
                        : 'Bon de livraison'}
                  </Button>
                </Box>
              </>
            )}

            {detailMainTab === 1 && (
              <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Modifiez les informations de l&apos;objet (photos, lieu, attributs…).
                </Typography>
                <Button variant="contained" onClick={onRequestEditFoundObject} disabled={!onRequestEditFoundObject}>
                  Ouvrir le formulaire de modification
                </Button>
              </Box>
            )}

            {detailMainTab === 2 && (
              <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
                {foundObjectData?.storage_box ? (
                  <>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      {foundObjectData.storage_box.name}
                    </Typography>
                    {foundObjectData.storage_box.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {foundObjectData.storage_box.description}
                      </Typography>
                    )}
                    {foundObjectData.storage_box.provider_item && (
                      <Chip
                        size="small"
                        label={foundObjectData.storage_box.provider_item.name}
                        variant="outlined"
                        sx={{ mr: 1 }}
                      />
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Aucune boîte de stockage associée. Assignez-en une depuis le formulaire de modification.
                  </Typography>
                )}
              </Box>
            )}

            {detailMainTab === 3 && (
              <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Statut envoi :{' '}
                  <strong>
                    {foundObjectData?.status === 'to_ship'
                      ? 'À expédier'
                      : foundObjectData?.status === 'in_transit'
                        ? 'En transit'
                        : foundObjectData?.status === 'claimed'
                          ? 'Restitué'
                          : foundObjectData?.status === 'found'
                            ? 'Trouvé'
                            : foundObjectData?.status || '—'}
                  </strong>
                </Typography>
                {colissimoEtiquetteUrl ? (
                  <Button variant="contained" href={colissimoEtiquetteUrl} target="_blank" rel="noreferrer">
                    Ouvrir l&apos;étiquette / bon d&apos;envoi
                  </Button>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Aucune étiquette Colissimo détectée dans la conversation. Utilisez « Envoyer bon de livraison »
                    dans l&apos;onglet Discuter pour lancer la procédure.
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>
        </Box>
      )}

      <Dialog open={confirmUndoOpen} onClose={() => setConfirmUndoOpen(false)}>
        <DialogTitle>Annuler la restitution ?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Cette action remettra l'objet en statut "Trouvé". Voulez-vous continuer ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmUndoOpen(false)} disabled={actionRunning}>
            Annuler
          </Button>
          <Button onClick={handleUndoClaimed} color="warning" variant="contained" disabled={actionRunning}>
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deliveryOfferDialogOpen}
        onClose={() => setDeliveryOfferDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Choisir le transporteur</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Le propriétaire choisira ensuite comment recevoir l&apos;objet (domicile ou point relais) avant de payer.
          </Typography>
          <ChooseDeliveryCarrierPanel
            value={deliveryOfferCarrier}
            onChange={setDeliveryOfferCarrier}
            pricing={deliveryPricing}
            locationProviderId={deliveryLocationProviderId}
            disabled={actionRunning || sendMutation.isPending}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeliveryOfferDialogOpen(false)} disabled={actionRunning}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={() => void sendDeliveryOffer()}
            disabled={actionRunning || sendMutation.isPending || !deliveryOfferCarrier}
          >
            Proposer ce transporteur
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmHandoverOpen}
        onClose={() => setConfirmHandoverOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Precautions avant restitution en main propre</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Avant de confirmer la restitution, verifiez les points suivants :
            </Typography>
            <Box component="ul" sx={{ mt: 0, pl: 2.5, mb: 0 }}>
              <li>
                <Typography variant="body2">
                  Controlez l'identite de la personne (piece d'identite si possible).
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Demandez des details secrets que seul le proprietaire connait (etat, marque, particularites,
                  accessoires, code PIN partiel, etc.).
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Ne montrez pas la photo ou des informations sensibles de l'objet avant verification.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Pour un telephone, demandez si possible de le faire sonner et/ou de le deverrouiller.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  En cas de doute, ne remettez pas l'objet et gardez le statut actuel.
                </Typography>
              </li>
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmHandoverOpen(false)} disabled={actionRunning}>
            Annuler
          </Button>
          <Button
            onClick={async () => {
              setConfirmHandoverOpen(false);
              await handleMarkClaimed();
            }}
            color="secondary"
            variant="contained"
            disabled={actionRunning}
          >
            Confirmer la restitution
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FoundObjectMessagesWorkspace;
